import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { hierarchy, pack } from "d3-hierarchy";
import { interpolateZoom } from "d3-interpolate";
import { ClipLoader } from "react-spinners";
import { AUTOPILOT_COLORS, autopilotSecondaryButtonStyle } from "../../../../components/autopilot/styles";
import { montarHierarquia, totaisPorDono, chegadaDoDocumento, rotuloChegada, DONOS } from "./montarHierarquia";

// PO1-3325 — Lupa de Inconsistências: a fila em bolhas (empacotamento de
// círculos), só o nível atual visível. Clique numa bolha desce (motivo →
// transportadora → documento), clique no fundo ou em "‹" sobe. A lista ao
// lado acompanha o nível; o tamanho da bolha é R$ travado ou quantidade; a
// cor é quem resolve, e a legenda filtra. "Revisar" devolve o recorte para a
// grade da tela (onRevisar).

const TOM_COR = {
  warning: AUTOPILOT_COLORS.statusWarning,
  info: AUTOPILOT_COLORS.statusInfo,
  success: AUTOPILOT_COLORS.statusSuccess,
  danger: AUTOPILOT_COLORS.statusDanger,
  neutral: AUTOPILOT_COLORS.muted,
};
const corDono = (dono) => TOM_COR[(DONOS[dono] || DONOS.misto).tom] || AUTOPILOT_COLORS.muted;

const fmtBRL = (v) => (v == null ? "-" : Number(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }));
const fmtCurto = (v) => {
  if (v == null) return "-";
  const n = Number(v);
  if (n >= 1000000) return "R$ " + (n / 1000000).toLocaleString("pt-BR", { maximumFractionDigits: 1 }) + " mi";
  if (n >= 1000) return "R$ " + (n / 1000).toLocaleString("pt-BR", { maximumFractionDigits: n >= 100000 ? 0 : 1 }) + " mil";
  return fmtBRL(n);
};
const fmtData = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "-" : d.toLocaleDateString("pt-BR");
};
const diasParado = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return Math.max(0, Math.round((Date.now() - d.getTime()) / 864e5));
};
const plural = (n, um, varios) => `${Number(n || 0).toLocaleString("pt-BR")} ${n === 1 ? um : varios}`;

const reducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const segStyle = {
  display: "inline-flex",
  border: `1px solid ${AUTOPILOT_COLORS.border}`,
  borderRadius: 10,
  overflow: "hidden",
};
const segBtn = (active) => ({
  border: "none",
  background: active ? "var(--theme-accent-glow, " + AUTOPILOT_COLORS.insetSurfaceActive + ")" : "transparent",
  color: active ? "var(--theme-accent)" : AUTOPILOT_COLORS.text,
  fontWeight: active ? 700 : 500,
  fontSize: "0.78rem",
  padding: "0.4rem 0.8rem",
  cursor: "pointer",
});

export default function LupaInconsistencias({ rows, catalogo, tipo, verTodos, loading, onRevisar, onFoco }) {
  const [medida, setMedida] = useState("valor");
  const [ocultos, setOcultos] = useState(() => new Set());
  const [focoKey, setFocoKey] = useState("raiz");
  const [docSel, setDocSel] = useState(null);
  const [tam, setTam] = useState(520);
  const roRef = useRef(null);
  const viewRef = useRef(null);
  const [view, setView] = useState(null);
  const animRef = useRef(null);

  // Tamanho do palco (quadrado) acompanha a largura disponível. Ref de callback:
  // o palco só existe depois do "carregando", e um efeito na montagem nunca o
  // media (ficava nos 520 iniciais — QA 05/10).
  // Também cabe na altura da janela, para a legenda não sair da tela.
  const stageRef = useCallback((el) => {
    if (roRef.current) {
      window.removeEventListener("resize", roRef.current.medir);
      roRef.current.disconnect();
      roRef.current = null;
    }
    if (!el) return;
    const medir = () => {
      const altura = Math.max(420, (window.innerHeight || 800) - 230);
      setTam(Math.max(320, Math.min(820, altura, Math.floor(el.clientWidth))));
    };
    medir();
    if (typeof ResizeObserver === "undefined") return;
    roRef.current = new ResizeObserver(medir);
    roRef.current.observe(el);
    roRef.current.medir = medir;
    window.addEventListener("resize", medir);
  }, []);
  useEffect(
    () => () => {
      if (!roRef.current) return;
      window.removeEventListener("resize", roRef.current.medir);
      roRef.current.disconnect();
    },
    []
  );

  const raiz = useMemo(
    () => montarHierarquia(rows || [], catalogo || {}, { tipo, medida, donosOcultos: ocultos, verTodos }),
    [rows, catalogo, tipo, medida, ocultos, verTodos]
  );
  const totais = useMemo(() => totaisPorDono(rows || [], catalogo || {}, tipo), [rows, catalogo, tipo]);

  const empacotado = useMemo(() => {
    const h = hierarchy(raiz)
      .sum((d) => (d.children && d.children.length ? 0 : Math.max(d.valor || 0, 1e-6)))
      .sort((a, b) => b.value - a.value);
    return pack().size([tam, tam]).padding((d) => (d.depth === 0 ? 10 : 5))(h);
  }, [raiz, tam]);

  const porKey = useMemo(() => {
    const m = new Map();
    empacotado.each((n) => m.set(n.data.key, n));
    return m;
  }, [empacotado]);

  const foco = porKey.get(focoKey) || empacotado;
  // Enquadra as bolhas visíveis (filhos do foco), não o círculo do foco: o
  // empacotamento deixa folga em volta e o grupo ficava pequeno (QA 05/10).
  const alvo = useMemo(() => {
    const filhos = foco.children || [];
    if (!filhos.length) return [foco.x, foco.y, foco.r * 2 + 8];
    let x0 = Infinity;
    let y0 = Infinity;
    let x1 = -Infinity;
    let y1 = -Infinity;
    filhos.forEach((c) => {
      x0 = Math.min(x0, c.x - c.r);
      y0 = Math.min(y0, c.y - c.r);
      x1 = Math.max(x1, c.x + c.r);
      y1 = Math.max(y1, c.y + c.r);
    });
    return [(x0 + x1) / 2, (y0 + y1) / 2, Math.max(x1 - x0, y1 - y0) * 1.04 + 4];
  }, [foco]);

  // Zoom: animado entre o retrato anterior e o novo (interpolateZoom), ou
  // direto quando o usuário prefere menos movimento ou o palco mudou.
  useEffect(() => {
    const de = viewRef.current;
    if (animRef.current) cancelAnimationFrame(animRef.current);
    if (!de || reducedMotion() || (de[2] === alvo[2] && de[0] === alvo[0] && de[1] === alvo[1])) {
      viewRef.current = alvo;
      setView(alvo);
      return undefined;
    }
    const interp = interpolateZoom(de, alvo);
    const dur = Math.min(900, Math.max(350, interp.duration));
    const t0 = performance.now();
    const passo = (t) => {
      const p = Math.min(1, (t - t0) / dur);
      const v = interp(p);
      viewRef.current = v;
      setView(v);
      if (p < 1) animRef.current = requestAnimationFrame(passo);
    };
    animRef.current = requestAnimationFrame(passo);
    return () => animRef.current && cancelAnimationFrame(animRef.current);
  }, [alvo]);

  // Foco que sumiu (filtro/aba mudou) volta para a raiz.
  useEffect(() => {
    if (!porKey.has(focoKey)) {
      setFocoKey("raiz");
      setDocSel(null);
    }
  }, [porKey, focoKey]);

  // Página da Lupa: a grade de baixo acompanha a bolha em foco (ou o documento
  // aberto no detalhe). Ref para não refazer o efeito quando o pai re-renderiza.
  const onFocoRef = useRef(onFoco);
  onFocoRef.current = onFoco;
  const recorteFoco = docSel ? docSel.recorte : foco.data.recorte;
  const chaveRecorte = JSON.stringify(recorteFoco || {});
  useEffect(() => {
    if (onFocoRef.current) onFocoRef.current(JSON.parse(chaveRecorte));
  }, [chaveRecorte]);

  const irPara = useCallback((no) => {
    setDocSel(null);
    setFocoKey(no ? no.data.key : "raiz");
  }, []);
  const subir = useCallback(() => {
    if (foco.parent) irPara(foco.parent);
  }, [foco, irPara]);
  const clicar = useCallback(
    (no) => {
      if (no.data.tipo === "documento") {
        setDocSel(no.data);
        if (no.parent !== foco) irPara(no.parent);
        return;
      }
      if (no.data.tipo === "outros") {
        if (onRevisar) onRevisar(no.data.recorte);
        return;
      }
      if (no === foco) {
        subir();
        return;
      }
      irPara(no);
    },
    [foco, irPara, subir, onRevisar]
  );

  const v = view || alvo;
  const k = tam / v[2];
  const visiveis = (foco.children || []).slice();
  const trilha = foco.ancestors().reverse();

  const toggleDono = (dono) => {
    setOcultos((prev) => {
      const next = new Set(prev);
      if (next.has(dono)) next.delete(dono);
      else next.add(dono);
      return next;
    });
    setFocoKey("raiz");
    setDocSel(null);
  };

  const docsDeNo = (no) => no.data.docs || 0;
  const explicacaoFoco = foco.data.explicacao || (foco.depth === 0
    ? tipo === "fatura"
      ? "Faturas retidas na integração. Clique numa bolha para ver o motivo e, dentro dele, o que segura cada uma."
      : "CT-e que tentaram entrar e ficaram na fila. Clique numa bolha para descer: motivo, transportadora, documento."
    : null);

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "2.2rem 0" }}>
        <ClipLoader size={26} color="var(--theme-accent)" />
      </div>
    );
  }
  if (!rows || !rows.length) {
    return (
      <div style={{ textAlign: "center", padding: "1.6rem 0", color: AUTOPILOT_COLORS.muted, fontSize: "0.85rem" }}>
        Nenhuma pendência no escopo atual.
      </div>
    );
  }

  const donosPresentes = Object.keys(DONOS).filter((d) => totais.porDono[d]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
      {/* trilha + controles */}
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: "0.6rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", minWidth: 0, flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={subir}
            disabled={!foco.parent}
            aria-label="Voltar um nível"
            style={{ ...autopilotSecondaryButtonStyle, padding: "0.25rem 0.6rem", opacity: foco.parent ? 1 : 0.4 }}
          >
            ‹
          </button>
          <nav aria-label="Nível atual" style={{ display: "flex", flexWrap: "wrap", gap: "0.25rem", alignItems: "center", fontSize: "0.82rem" }}>
            {trilha.map((n, i) => (
              <React.Fragment key={n.data.key}>
                {i > 0 && <span style={{ color: AUTOPILOT_COLORS.muted }}>›</span>}
                {i < trilha.length - 1 ? (
                  <button
                    type="button"
                    onClick={() => irPara(n)}
                    style={{ border: "none", background: "transparent", color: AUTOPILOT_COLORS.muted, cursor: "pointer", padding: "0 0.15rem" }}
                  >
                    {n.data.nome}
                  </button>
                ) : (
                  <span style={{ color: AUTOPILOT_COLORS.title, fontWeight: 600 }}>{n.data.nome}</span>
                )}
              </React.Fragment>
            ))}
          </nav>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
          <span style={{ fontSize: "0.76rem", color: AUTOPILOT_COLORS.muted }}>Tamanho da bolha:</span>
          <div style={segStyle} role="group" aria-label="Tamanho da bolha">
            {[
              { key: "valor", label: "R$ travado" },
              { key: "quantidade", label: "Quantidade" },
            ].map((opt) => (
              <button key={opt.key} type="button" aria-pressed={medida === opt.key} onClick={() => setMedida(opt.key)} style={segBtn(medida === opt.key)}>
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem", alignItems: "stretch" }}>
        {/* lista do nível atual */}
        <aside style={{ flex: "1 1 280px", minWidth: 0, maxWidth: 420, display: "flex", flexDirection: "column", gap: "0.5rem" }}>
          <div>
            <div style={{ color: AUTOPILOT_COLORS.title, fontWeight: 700, fontSize: "0.95rem" }}>{foco.data.nome}</div>
            <div style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.78rem", fontVariantNumeric: "tabular-nums" }}>
              {plural(docsDeNo(foco), "documento", "documentos")}
              {medida === "valor" ? (foco.data.valorNaoLido ? " · valor não lido" : ` · ${fmtBRL(foco.data.valorReal)}`) : ""}
            </div>
            {explicacaoFoco && <div style={{ color: AUTOPILOT_COLORS.text, fontSize: "0.8rem", marginTop: "0.35rem", lineHeight: 1.4 }}>{explicacaoFoco}</div>}
            {foco.depth > 0 && onRevisar && (
              <button
                type="button"
                onClick={() => onRevisar(foco.data.recorte)}
                style={{ ...autopilotSecondaryButtonStyle, marginTop: "0.5rem", fontSize: "0.78rem" }}
              >
                Revisar na lista
              </button>
            )}
          </div>

          {docSel && <DetalheDocumento doc={docSel} tipo={tipo} catalogo={catalogo} onFechar={() => setDocSel(null)} onRevisar={onRevisar} />}

          <div role="list" style={{ display: "flex", flexDirection: "column", gap: 2, maxHeight: 440, overflowY: "auto" }}>
            {visiveis.map((n) => {
              const cor = corDono(n.data.dono);
              const max = Math.max(...visiveis.map((x) => (medida === "valor" ? x.data.valor : x.data.docs))) || 1;
              const frac = (medida === "valor" ? n.data.valor : n.data.docs) / max;
              const ativo = docSel && n.data.tipo === "documento" && n.data.key === docSel.key;
              return (
                <div
                  key={n.data.key}
                  role="listitem"
                  tabIndex={0}
                  onClick={() => clicar(n)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      clicar(n);
                    }
                  }}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "10px 1fr auto",
                    gap: "0.55rem",
                    alignItems: "center",
                    padding: "0.4rem 0.5rem",
                    borderRadius: 8,
                    cursor: "pointer",
                    background: ativo ? AUTOPILOT_COLORS.insetSurfaceActive : "transparent",
                  }}
                >
                  <span style={{ width: 10, height: 10, borderRadius: "50%", background: cor }} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: "0.84rem", fontWeight: 500, color: AUTOPILOT_COLORS.title, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {n.data.nome}
                    </div>
                    {n.data.tipo !== "documento" && (
                      <div style={{ fontSize: "0.72rem", color: AUTOPILOT_COLORS.muted }}>{plural(n.data.docs, "documento", "documentos")}</div>
                    )}
                    {n.data.tipo === "documento" && (
                      <div style={{ fontSize: "0.72rem", color: AUTOPILOT_COLORS.muted }}>
                        {n.data.doc.fantasiaGrupo || n.data.doc.Fantasia || ""}
                        {diasParado(chegadaDoDocumento(n.data.doc)) != null ? ` · ${plural(diasParado(chegadaDoDocumento(n.data.doc)), "dia", "dias")}` : ""}
                      </div>
                    )}
                    <div style={{ height: 3, borderRadius: 2, background: AUTOPILOT_COLORS.progressTrack, marginTop: 4, overflow: "hidden" }}>
                      <span style={{ display: "block", height: "100%", width: `${Math.max(2, frac * 100)}%`, background: cor }} />
                    </div>
                  </div>
                  <div style={{ textAlign: "right", fontSize: "0.78rem", fontVariantNumeric: "tabular-nums", color: AUTOPILOT_COLORS.text, whiteSpace: "nowrap" }}>
                    {n.data.valorNaoLido ? "valor não lido" : fmtBRL(n.data.valorReal)}
                    {n.data.valorEstimado ? <span title="Soma dos CT-e citados (total do arquivo veio zerado)"> ≈</span> : null}
                  </div>
                </div>
              );
            })}
          </div>
        </aside>

        {/* bolhas */}
        <section ref={stageRef} aria-label="Mapa de bolhas" style={{ flex: "2 1 420px", minWidth: 0, position: "relative" }}>
          <svg
            viewBox={`${-tam / 2} ${-tam / 2} ${tam} ${tam}`}
            width="100%"
            height={tam}
            style={{ display: "block", cursor: foco.parent ? "zoom-out" : "default" }}
            onClick={subir}
          >
            {visiveis.map((n) => {
              const cor = corDono(n.data.dono);
              const x = (n.x - v[0]) * k;
              const y = (n.y - v[1]) * k;
              const r = n.r * k;
              const nome = n.data.nome;
              const fs = Math.max(10, Math.min(22, r / 4.4));
              const maxCh = Math.max(4, Math.floor((r * 1.7) / (fs * 0.55)));
              const linhas = quebrar(nome, maxCh);
              const mostraQtd = r > 34 && n.data.tipo !== "documento";
              const y0 = (mostraQtd ? -(linhas.length - 1) * fs * 0.55 - fs * 0.35 : -(linhas.length - 1) * fs * 0.55 + fs * 0.35);
              return (
                <g
                  key={n.data.key}
                  transform={`translate(${x},${y})`}
                  onClick={(e) => {
                    e.stopPropagation();
                    clicar(n);
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <title>
                    {`${nome} · ${plural(n.data.docs, "documento", "documentos")}${n.data.valorReal != null ? " · " + fmtBRL(n.data.valorReal) : ""}`}
                  </title>
                  <circle r={r} fill={cor} fillOpacity={0.16} stroke={cor} strokeOpacity={0.8} strokeWidth={1.5} />
                  {r >= 16 && (
                    <text textAnchor="middle" style={{ fill: AUTOPILOT_COLORS.title, fontWeight: 600, fontSize: fs, pointerEvents: "none" }}>
                      {linhas.map((ln, i) => (
                        <tspan key={i} x={0} y={y0 + i * fs * 1.1}>
                          {ln}
                        </tspan>
                      ))}
                    </text>
                  )}
                  {mostraQtd && (
                    <text
                      textAnchor="middle"
                      y={y0 + linhas.length * fs * 1.1 + 2}
                      style={{ fill: AUTOPILOT_COLORS.muted, fontSize: Math.max(9, fs * 0.72), pointerEvents: "none" }}
                    >
                      {plural(n.data.docs, "documento", "documentos")}
                    </text>
                  )}
                </g>
              );
            })}
          </svg>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 4 }}>
            {donosPresentes.map((d) => (
              <button
                key={d}
                type="button"
                aria-pressed={!ocultos.has(d)}
                onClick={() => toggleDono(d)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: "0.74rem",
                  padding: "3px 9px",
                  borderRadius: 999,
                  border: `1px solid ${AUTOPILOT_COLORS.border}`,
                  background: AUTOPILOT_COLORS.insetSurface,
                  color: AUTOPILOT_COLORS.text,
                  opacity: ocultos.has(d) ? 0.45 : 1,
                  cursor: "pointer",
                }}
              >
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: corDono(d) }} />
                {DONOS[d].nome}
                <span style={{ color: AUTOPILOT_COLORS.muted, fontVariantNumeric: "tabular-nums" }}>
                  {totais.porDono[d].docs}
                  {medida === "valor" ? ` · ${fmtCurto(totais.porDono[d].valor)}` : ""}
                </span>
              </button>
            ))}
          </div>
        </section>
      </div>

      <div style={{ fontSize: "0.78rem", color: AUTOPILOT_COLORS.muted, fontVariantNumeric: "tabular-nums" }}>
        {plural(totais.total, tipo === "fatura" ? "fatura" : "CT-e", tipo === "fatura" ? "faturas" : "CT-e")} · {fmtBRL(totais.valorTotal)}
        {raiz.docsUnicos !== totais.total ? ` · ${plural(raiz.docsUnicos, "visível", "visíveis")} com o filtro` : ""}
      </div>
    </div>
  );
}

function quebrar(texto, maxCh) {
  const palavras = String(texto || "").split(" ");
  const linhas = [""];
  for (const p of palavras) {
    const atual = linhas[linhas.length - 1];
    if ((atual + " " + p).trim().length <= maxCh) linhas[linhas.length - 1] = (atual + " " + p).trim();
    else if (linhas.length < 2) linhas.push(p);
    else {
      linhas[1] = (linhas[1] + " " + p).slice(0, Math.max(1, maxCh - 1)) + "…";
      break;
    }
  }
  return linhas.filter((l) => l);
}

function DetalheDocumento({ doc, tipo, catalogo, onFechar, onRevisar }) {
  const row = doc.doc || {};
  const dias = diasParado(chegadaDoDocumento(row));
  const rotulo = (m) => (catalogo && catalogo[m] && catalogo[m].rotulo) || m;
  return (
    <div style={{ border: `1px solid ${AUTOPILOT_COLORS.border}`, borderRadius: 10, padding: "0.65rem 0.75rem", background: AUTOPILOT_COLORS.insetSurface, display: "flex", flexDirection: "column", gap: "0.35rem" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.5rem" }}>
        <div style={{ color: AUTOPILOT_COLORS.title, fontWeight: 700, fontSize: "0.9rem" }}>{doc.nome}</div>
        <button type="button" onClick={onFechar} aria-label="Fechar detalhe" style={{ border: "none", background: "transparent", color: AUTOPILOT_COLORS.muted, cursor: "pointer" }}>
          ×
        </button>
      </div>
      <dl style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "2px 10px", fontSize: "0.78rem", margin: 0 }}>
        <dt style={{ color: AUTOPILOT_COLORS.muted }}>Transportadora</dt>
        <dd style={{ margin: 0 }}>{row.fantasiaGrupo || row.Fantasia || "-"}</dd>
        <dt style={{ color: AUTOPILOT_COLORS.muted }}>Valor</dt>
        <dd style={{ margin: 0, fontVariantNumeric: "tabular-nums" }}>
          {doc.valorNaoLido ? "não lido no arquivo" : fmtBRL(row.valor)}
          {doc.valorEstimado ? " (soma dos CT-e citados)" : ""}
        </dd>
        <dt style={{ color: AUTOPILOT_COLORS.muted }}>Emissão</dt>
        <dd style={{ margin: 0 }}>{fmtData(row.Dat_Emissao)}</dd>
        {dias != null && (
          <>
            <dt style={{ color: AUTOPILOT_COLORS.muted }}>{rotuloChegada(row)}</dt>
            <dd style={{ margin: 0 }}>{plural(dias, "dia", "dias")}</dd>
          </>
        )}
        <dt style={{ color: AUTOPILOT_COLORS.muted }}>Arquivo</dt>
        <dd style={{ margin: 0, wordBreak: "break-all" }}>{row.NomeArquivo || "-"}</dd>
      </dl>
      <div style={{ fontSize: "0.78rem" }}>
        <div style={{ color: AUTOPILOT_COLORS.muted }}>{tipo === "fatura" ? "Motivo da fatura" : "Motivos"}</div>
        {(row.motivos || []).map((m) => (
          <div key={m.motivo} style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: corDono(m.dono) }} />
            {rotulo(m.motivo)}
          </div>
        ))}
      </div>
      {tipo === "fatura" && Array.isArray(row.ctes) && row.ctes.length > 0 && (
        <div style={{ fontSize: "0.78rem" }}>
          <div style={{ color: AUTOPILOT_COLORS.muted }}>
            CT-e que seguram a fatura ({row.ctes.length}
            {row.ctesResumo ? ` de ${row.ctesResumo.total}; ${row.ctesResumo.integrados} já integrados` : ""})
          </div>
          <div style={{ maxHeight: 140, overflowY: "auto" }}>
            {row.ctes.map((c) => (
              <div key={`${c.numero}-${c.serie}`} style={{ display: "flex", alignItems: "center", gap: 6, fontVariantNumeric: "tabular-nums" }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: corDono(c.motivos && c.motivos[0] ? c.motivos[0].dono : "misto") }} />
                <span>CT-e {c.numero}</span>
                <span style={{ color: AUTOPILOT_COLORS.muted }}>· {c.motivos && c.motivos[0] ? rotulo(c.motivos[0].motivo) : c.estado}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {onRevisar && (
        <button type="button" onClick={() => onRevisar(doc.recorte)} style={{ ...autopilotSecondaryButtonStyle, fontSize: "0.76rem", alignSelf: "flex-start" }}>
          Abrir na lista
        </button>
      )}
    </div>
  );
}
